import { useFetcher } from '@remix-run/react'
import { LucideLock, LucidePaperclip } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import {
  ValidatedForm,
  useControlField,
  useFormContext,
} from 'remix-validated-form'
import { aCookieContact, aListingContact, useAtom, useSetAtom } from '~/atoms'
import { LoadingCircle, TextField } from '~/components'
import { Button, Dialog, Textarea, toast } from '~/components/ui'
import {
  ActivityContact,
  CookieContact,
  Listing,
  ListingFile,
  brokerContactValidator,
} from '~/models'

function FileDownloadDialog({
  listing,
  file,
  isOpen,
  setIsOpen,
}: {
  listing: Listing
  file: ListingFile
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}) {
  const setListingContact = useSetAtom(aListingContact)
  const [cookieContact, setCookieContact] = useAtom(aCookieContact)
  const fetcher = useFetcher<{
    success: boolean
    fileUrl?: string
    listingContact: ActivityContact
    cookieContact: CookieContact
  }>()
  const formId = 'file-download-form'
  const formContext = useFormContext(formId)
  const [value, setValue] = useControlField<string>('message', formId)

  const responseHandled = useRef(false)
  useEffect(() => {
    if (fetcher.data && responseHandled.current === false) {
      responseHandled.current = true
      setIsOpen(false)
      if (fetcher.data?.success && fetcher.data?.fileUrl) {
        window.open(fetcher.data.fileUrl, '_blank')
        if (fetcher.data.listingContact) {
          setListingContact(fetcher.data.listingContact)
        }
        if (fetcher.data.cookieContact) {
          setCookieContact(fetcher.data.cookieContact)
        }
      } else {
        toast({
          title: 'Error Submitting Form',
          variant: 'destructive',
          description:
            'There was a problem submitting the form. Please try again or contact support.',
        })
      }
    }
  }, [fetcher])

  const contactForm = (
    <ValidatedForm
      id={formId}
      validator={brokerContactValidator}
      method="post"
      action="/api/actions/contact-broker"
      fetcher={fetcher}
      defaultValues={cookieContact ?? undefined}>
      <div className="mb-2 grid grid-cols-12 gap-6">
        <input type="hidden" name="_id" value={listing._id} />
        <input type="hidden" name="fileId" value={file._id} />
        <TextField
          name="firstName"
          placeholder="First Name*"
          className="col-span-6"
          disabled={fetcher.state === 'submitting'}
        />
        <TextField
          name="lastName"
          placeholder="Last Name*"
          className="col-span-6"
          disabled={fetcher.state === 'submitting'}
        />
        <TextField
          name="email"
          placeholder="Email*"
          type="email"
          className="col-span-6"
          disabled={fetcher.state === 'submitting'}
        />
        <TextField
          name="phone"
          placeholder="Phone"
          type="tel"
          className="col-span-6"
          disabled={fetcher.state === 'submitting'}
        />
        <Textarea
          name="message"
          placeholder="Message"
          className="col-span-12"
          disabled={fetcher.state === 'submitting'}
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
      </div>
    </ValidatedForm>
  )

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <Dialog.Content className="sm:max-w-[500px]">
        <Dialog.Header>
          <Dialog.Title className="mb-3 font-normal">Unlock File</Dialog.Title>
          <Dialog.Description>
            Enter your name, email and phone number to download this file. The
            listing brokers will be notified of your download.
          </Dialog.Description>
        </Dialog.Header>

        {contactForm}

        <Dialog.Footer>
          <Button
            type="submit"
            disabled={fetcher.state !== 'idle'}
            onClick={async (e) => {
              e.preventDefault()
              formContext.submit()
            }}>
            {fetcher.state !== 'idle' && (
              <LoadingCircle className="mr-2 h-5 w-5" />
            )}
            {fetcher.state !== 'idle' ? 'Submitting' : 'Submit'}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default function ProtectedFile({
  file,
  listing,
}: {
  file: ListingFile
  listing: Listing
}) {
  const [isOpen, setIsOpen] = useState<boolean>(false)

  return (
    <>
      <li>
        <button
          className="flex w-full items-center justify-between py-3 pl-3 pr-4 text-left text-base hover:bg-muted"
          onClick={() => setIsOpen(true)}>
          <div className="flex w-0 flex-1 items-center">
            <LucidePaperclip
              className="h-5 w-5 flex-shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
            <span className="ml-4 w-0 flex-1 truncate">
              {file.title || 'Listing Brochure'}
            </span>
          </div>
          <div className="ml-4 flex-shrink-0">
            <LucideLock className="h-5 w-5 print:hidden" />
          </div>
        </button>
      </li>
      <FileDownloadDialog
        listing={listing}
        file={file}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />
    </>
  )
}
